// Видимость и ширина боковых панелей рабочей области.
// Это настройки вида, а не часть AppState: они не попадают в экспорт JSON
// и не уезжают на сервер, поэтому хранятся отдельным ключом в браузере.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { createStorage, type StorageAdapter } from '../../store';

export const PANELS_STORAGE_KEY = 'plan-flights.panels.v1';

/** Границы ручного ресайза, px. */
export const PANEL_MIN_W = 200;
export const PANEL_MAX_W = 560;

export interface PanelLayout {
  libraryOpen: boolean;
  compareOpen: boolean;
  /** null — ширина не задана вручную, работают значения из tokens.css */
  libraryWidth: number | null;
  compareWidth: number | null;
  toggleLibrary: () => void;
  toggleCompare: () => void;
  setLibraryWidth: (px: number) => void;
  setCompareWidth: (px: number) => void;
}

interface PanelsSnapshot {
  libraryOpen: boolean;
  compareOpen: boolean;
  libraryWidth: number | null;
  compareWidth: number | null;
}

const DEFAULT_PANELS: PanelsSnapshot = {
  libraryOpen: true,
  compareOpen: true,
  libraryWidth: null,
  compareWidth: null,
};

export function clampPanelWidth(px: number): number {
  if (!Number.isFinite(px)) return PANEL_MIN_W;
  return Math.round(Math.max(PANEL_MIN_W, Math.min(PANEL_MAX_W, px)));
}

function readWidth(raw: unknown): number | null {
  return typeof raw === 'number' && Number.isFinite(raw) ? clampPanelWidth(raw) : null;
}

/** Любой мусор в хранилище трактуем как «обе панели открыты, ширины по умолчанию». */
function readPanels(storage: StorageAdapter): PanelsSnapshot {
  const raw = storage.get(PANELS_STORAGE_KEY);
  if (!raw) return DEFAULT_PANELS;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return DEFAULT_PANELS;
    const rec = parsed as Record<string, unknown>;
    return {
      libraryOpen:
        typeof rec.libraryOpen === 'boolean' ? rec.libraryOpen : DEFAULT_PANELS.libraryOpen,
      compareOpen:
        typeof rec.compareOpen === 'boolean' ? rec.compareOpen : DEFAULT_PANELS.compareOpen,
      libraryWidth: readWidth(rec.libraryWidth),
      compareWidth: readWidth(rec.compareWidth),
    };
  } catch {
    return DEFAULT_PANELS;
  }
}

export function usePanelLayout(): PanelLayout {
  const storage = useMemo(() => createStorage(), []);
  const [panels, setPanels] = useState<PanelsSnapshot>(() => readPanels(storage));

  useEffect(() => {
    storage.set(PANELS_STORAGE_KEY, JSON.stringify(panels));
  }, [storage, panels]);

  const toggleLibrary = useCallback(() => {
    setPanels((prev) => ({ ...prev, libraryOpen: !prev.libraryOpen }));
  }, []);

  const toggleCompare = useCallback(() => {
    setPanels((prev) => ({ ...prev, compareOpen: !prev.compareOpen }));
  }, []);

  const setLibraryWidth = useCallback((px: number) => {
    setPanels((prev) => ({ ...prev, libraryWidth: clampPanelWidth(px) }));
  }, []);

  const setCompareWidth = useCallback((px: number) => {
    setPanels((prev) => ({ ...prev, compareWidth: clampPanelWidth(px) }));
  }, []);

  return { ...panels, toggleLibrary, toggleCompare, setLibraryWidth, setCompareWidth };
}
